import { faker } from "@faker-js/faker";
import { Factory } from "fishery";
import { DateTime } from "luxon";
import {
  Position,
  PositionDistribution,
  PositionDistributionBatch,
  PositionPaymentPref,
} from "../components/DistributionWorkflow/StepComponents/SelectPositionsStep/types";
import {
  CurrencyCode,
  InvestorDiligenceStatus,
  PaymentMethodEnum,
  VerificationStatus,
} from "../graphql/supergraphOperations";
import { toGlobalId } from "../utils/graphql_utils";
import { walletFactory } from "./money.factory";

class PositionPaymentPrefFullFactory extends Factory<PositionPaymentPref["paymentPrefFull"]> {
  ach() {
    return this.params({
      __typename: "AchPaymentPrefObjectType",
    });
  }

  wire() {
    return this.params({
      __typename: "WirePaymentPrefObjectType",
      referenceToBeneficiary: "",
      wireBbi: {
        field0: "",
        field1: "",
        field2: "",
        field3: "",
      },
    });
  }

  check() {
    return this.params({
      __typename: "CheckPaymentPrefObjectType",
    });
  }
}

export const positionPaymentPrefFullFactory = PositionPaymentPrefFullFactory.define(
  ({ sequence }) => {
    return {
      paymentPrefId: sequence,
    };
  },
);

export const watchlistDiligenceFactory = Factory.define<
  PositionPaymentPref["watchlistStatus"][0]
>(({ sequence }) => {
  return {
    id: sequence.toString(),
    status: InvestorDiligenceStatus.Confirmed,
    __typename: "ApiFirstWatchlistDiligenceObjectType",
  };
});

export const positionPaymentPrefFactory = Factory.define<PositionPaymentPref>(
  ({ sequence }) => {
    return {
      id: toGlobalId("PaymentPrefObjectType", sequence),
      paymentMethod: PaymentMethodEnum.Wire,
      bankInfoRedacted: `Bank of America····${faker.number.int({ min: 1000, max: 9999 })}`,
      verificationStatus: VerificationStatus.Verified,
      watchlistStatus: [watchlistDiligenceFactory.build()],
      paymentPrefFull: positionPaymentPrefFullFactory.wire().build(),
      __typename: "PaymentPrefObjectType" as const,
    };
  },
);

export const positionFactory = Factory.define<Position>(({ sequence }) => {
  return {
    id: toGlobalId("PositionObjectType", sequence),
    entityCurrency: CurrencyCode.Usd,
    account: {
      id: toGlobalId("AccountObjectType", sequence),
      gpTitle: faker.company.name(),
    },
    hasPendingPaymentPrefChangeRequests: false,
    hasPassedPicrComplianceChecks: true,
    paymentPrefUpdatedAt: DateTime.fromJSDate(
      faker.date.recent({ refDate: DateTime.now().minus({ days: 2 }).toJSDate() }),
    ).toISODate(), // Set paymentPrefUpdatedAt to be at least 2 days ago
    // This makes it so that by default, the payment instructions recently updated tag is not shown
    paymentPref: positionPaymentPrefFactory.build(),
    __typename: "PositionObjectType" as const,
  };
});

export const positionDistributionFactory = Factory.define<PositionDistribution>(
  ({ sequence }) => {
    return {
      id: toGlobalId("DistributionObjectType", sequence),
      amountDistributed: walletFactory.build(),
      netAmountAdjustments: walletFactory.build(),
      amountPayable: walletFactory.build(),
      hasAutomatedPayment: false,
      hasPayment: false,
      position: positionFactory.build(),
      __typename: "DistributionObjectType" as const,
    };
  },
);

export const positionDistributionBatchFactory = Factory.define<PositionDistributionBatch>(
  ({ sequence }) => {
    return {
      id: toGlobalId("DistributionBatchObjectType", sequence),
      description: faker.lorem.sentence(),
      entity: {
        __typename: "AccountObjectType",
        id: sequence.toString(),
        name: faker.company.name(),
        currency: {
          id: sequence.toString(),
          code: CurrencyCode.Usd,
        },
      },
      instances: positionDistributionFactory.buildList(202),
      __typename: "DistributionBatchObjectType" as const,
    };
  },
);
